<template>
    <div>
        <table-page
            v-show="!addingOrgLevels"
            :client="client"
            @updated="getOrgs"
            :warnings="warnings"
            :changelogs="changelogs"
            :hideTabs="true"
        >
            <fast-table
                :recordLabel="recordLabel"
                :sections="sections"
                :headers="headers"
                :data="tableData"
                :activeRowId="currentOrgId"
                @click="onRowClick($event)"
            >
                <template v-slot:table-actions>
                    <button class="btn btn-primary btn-collapse" id="addOrgButton" @click="addingOrgLevels = true">
                        <i class="far fa-fw fa-tools"></i> <span class="btn-text">Setup Org Levels</span>
                    </button>
                </template>
            </fast-table>

        </table-page>

        <div v-if="addingOrgLevels" class="flex-body body-scroll">
            <div class="container-fluid">
                <div class="container-centered-m py-4">
                    <h3>Organizational Levels</h3>

                    <p v-if="step == 'levels'">Create the names of the organizational levels and place them in order.</p>
                    <p v-if="step == 'options'">Create the value options for each organizational level.</p>

                    <div>
                        <div v-if="step == 'levels'">
                            <VueDraggable
                                v-model="formData.org_levels"
                                draggable=".draggable"
                                handle=".handle"
                                animation="200"
                                ghost-class="ghost"
                            >
                                <div v-for="(level, idx) in formData.org_levels" class="draggable">
                                    <form-input class="w-90" type="text" :nocaps="true" :draggable="true" :disabled="level.is_location" :modelValue="level.label" @update:model-value="formData.org_levels[idx].label = $event" placeholder="Level Name" @delete="onOptionDelete(idx)"/>
                                </div>
                                <a href class="btn btn-link" @click.prevent="addOrgLevel()"><i class="far fa-plus mr-hf"></i>Add Another Level</a>

                            </VueDraggable>
                        </div>
                        <div v-if="step == 'options'">
                            <div class="row mb-2" v-for="(level, levelIdx) in formData.org_levels" v-show="!level.is_location" >
                                <div class="col-12">
                                    <div class="card">
                                        <div class="card-header pr-1 d-flex align-items-center">
                                            <span class="btnlike mr-auto">
                                                <h4 class="mb-0">
                                                    {{ level.label }}
                                                </h4>
                                            </span>
                                        </div>
                                        <div class="card-body">
                                            <form-input v-for="(option, optionIdx) in level.options" class="w-90" type="text" :nocaps="true" :modelValue="option" @update:model-value="formData.org_levels[levelIdx].options[optionIdx]=$event" :placeholder="'Option ' + String(optionIdx + 1)" @delete="onOptionDelete(idx)"/>
                                            <a href class="btn btn-link" @click.prevent="addOrgLevelOption(levelIdx)"><i class="far fa-plus mr-hf"></i>Add Another Level</a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <form>
                            <form-errors :errors="formErrors"/>

                            <div class="row mb-2 mt-2 d-flex align-items-center">
                                <div class="col">
                                    <button v-if="step == 'levels'" @click="addingOrgLevels = false" class="btn-outline-primary btn">Back</button>
                                    <button v-if="step == 'options'" @click="step='levels'" class="btn-outline-primary btn">Back</button>
                                </div>

                                <div class="col text-right">
                                    <button v-if="step == 'levels'" class="btn btn-primary" @click.prevent="step='options'">Continue</button>
                                    <button v-if="step == 'options'" class="btn btn-primary" @click.prevent="onSubmit">Save</button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>

        <org-level-sidebar
            v-if="currentOrg"
            :client="client"
            :orgs="orgs"
            :level="currentOrg.org_level"
            ref="orgLevelSidebar"
            @updated="getOrgs"
            @close="currentOrgId = null"
        >
        </org-level-sidebar>

        <location-sidebar
            v-if="currentLocation"
            :client="client"
            :location-id="currentLocation.id"
            ref="locationSidebar"
            @updated="getOrgs"
            @close="currentLocationId = null"
        >
        </location-sidebar>
    </div>
</template>

<script>
import { VueDraggable } from 'vue-draggable-plus'
import TablePageMixin from '@/mixins/TablePageMixin'
import FormMixin from '@/mixins/Form'
import FastTable from '@/components/FastTable'
import OrgLevelSidebar from './components/OrgLevels/OrgLevelSidebar'
import LocationSidebar from './components/Locations/LocationSidebar'

export default {
    components: {FastTable, OrgLevelSidebar, LocationSidebar, VueDraggable},
    mixins: [TablePageMixin, FormMixin],
    props: ['client'],
    computed: {
        currentOrg() {
            return this.orgs.find(o => (o.id == this.currentOrgId))
        },
        currentLocation() {
            return this.locations.find(l => (l.id == this.currentLocationId))
        },
        sections() {
            let sections = [
                {title: 'Active', defaultIsClosed: false, id: 'active', hasDateFilter: false},
                {title: 'Inactive', defaultIsClosed: true, id: 'inactive', hasDateFilter: true},
            ]
            return sections
        },
        tableData() {
            const tableData = []
            const subsets = [
                this.activeOrgs,
                this.inactiveOrgs
            ]

            subsets.forEach(orgs => {
                const sectionData = []

                orgs.forEach(org => {
                    org.type = 'CompanyOrg'
                    sectionData.push({
                        id: org.id,
                        object: org,
                        cells: this.makeOrgTableData(org),
                    })
                })
                tableData.push(sectionData)
            })

            if (this.client.location_org_level) {
                this.locations.filter(l => l.is_active).forEach(l => {
                    l.type = 'WorkLocation'
                    tableData[0].push(
                        {
                            id: l.id,
                            object: l,
                            cells: this.makeLocationTableData(l)
                        }
                    )
                })
            }

            return tableData
        },
        activeOrgs() {
            // TODO KB CS add is_active to CompanyOrg
            return this.orgs
            // return this.orgs.filter((p) => {return p.is_active})
        },
        inactiveOrgs() {
            return []
            // return this.orgs.filter((p) => {return !p.is_active})
        },
    },
    data() {
        let data = {
            orgs: [],
            locations: [],
            dataLoaded: false,
            currentOrgId: null,
            currentLocationId: null,
            recordLabel: "Orgs",
            headers: [
                {label: 'Level Name', classes: '', isSearchable: true, defaultSort: false},
                {label: 'Option Value', classes: '', isSearchable: true, isFilterable: true},
                {label: 'Level', classes: '', isSearchable: false, defaultSort: true},
                {label: 'Locations', classes: '', isSearchable: false, isFilterable: false},
            ],
            addingOrgLevels: false,
            formData: this.makeFormData(),
            errors: {},
            formErrors: [],
            step: 'levels',
        }
        return data
    },
    mounted() {
        this.$store.dispatch('SET_PAGE_TITLE', 'Settings / Organizational Levels')
        this.getOrgs()
    },
    methods: {
        getOrgs() {
            this.$store.dispatch('START_LOADING')
            this.$api.get(`/clients/${this.client.id}/settings/company-orgs`).then(resp => {
                this.orgs = resp
                this.formData = this.makeFormData()
                this.$api.get(`/clients/${this.client.id}/settings/locations`).then(resp => {
                    this.locations = resp
                    this.$store.dispatch('STOP_LOADING')
                })
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                if (errors.__status__ == 403) {
                    this.$bus.$emit('no-access')
                }
                else {
                    this.$bus.showError(errors.__all__)
                }
            })
        },
        makeOrgTableData(org) {
            let data = [
                this.client[`org_level_${org.org_level}_label`],
                org.org_level_description,
                org.org_level,
                org.work_locations.length
            ]
            return data
        },
        makeLocationTableData(location) {
            let data = [
                'Location',
                location.name,
                this.client.location_org_level,
                '-'
            ]
            return data
        },
        makeFormData() {
            let data = {
                org_levels: []
            }
            let foundLocation = false
            for (let x = 1; x <= 5; x++) {
                let orgLevelKey = `org_level_${x}_label`
                let orgLevelLabel = this.client[orgLevelKey]
                if (orgLevelLabel) {
                    let isLocation = x == this.client.location_org_level
                    if (isLocation) {
                        orgLevelLabel = 'Company / Location'
                        foundLocation = true
                    }
                    data.org_levels.push({label: orgLevelLabel, options: [], is_location: isLocation})
                }
            }
            if (data.org_levels.length && this.orgs) {
                this.orgs.forEach(org => {
                    let idx = org.org_level - 1
                    data.org_levels[idx].options.push(org.org_level_description)
                })
                if (!foundLocation) {
                    data.org_levels.unshift({label: 'Company / Location', options: [], is_location: true})
                }
            } else if (!data.org_levels.length) {
                data.org_levels = [{label: 'Company / Location', options: [], is_location: true}, {label: '', options: ['', ''], is_location: false}]
            }
            return data
        },
        addOrgLevel() {
            this.formData.org_levels.push({'level': '', 'options': ['', ''], is_location: false})
        },
        addOrgLevelOption(idx) {
            this.formData.org_levels[idx].options.push('')
        },
        getFormData() {
            return this.formData
        },
        onRowClick(evt) {
            if (evt.type == 'CompanyOrg') {
                this.currentLocationId = null
                this.currentOrgId = evt.id
            } else if (evt.type == 'WorkLocation') {
                this.currentOrgId = null
                this.currentLocationId = evt.id
            }
        },
        onSubmit() {
            this.$store.dispatch('START_LOADING')
            return this.$api.post(`/clients/${this.client.id}/settings/company-orgs`, this.getFormData()).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.$emit('updated')
            }).catch((errors) => {
                console.log(errors)
                this.$store.dispatch('STOP_LOADING')

                this.formErrors = (errors.__all__ || [])
                this.$bus.showError('Please check the form for any missing fields.')
            })
        },
    },
}
</script>
